import { MenuItem, Stack, TextField, Typography } from '@mui/material'
import { useState } from 'react'
import FuturesEvidencePanel from './FuturesEvidencePanel'

type BankMode = 'cash-in' | 'bank-statement'
type BankName = 'Vietinbank' | 'MBbank' | 'BIDV'

interface Props {
  mode: BankMode
}

const modeMeta = {
  'cash-in': {
    title: 'Bank Cash In',
    accessLabel: 'Cash-in',
    target: 'frmFuturesMainSubFormBank -> frmFuturesQryBankCashIn',
    description:
      'Cash in/out list using the shared bank subform. Bank selection changes the Access query and may switch to a bank-specific form.',
    evidence: [
      'form_frmFuturesMainSubFormBank.txt',
      'form_frmFuturesQryBankCashIn.txt',
      'form_frmFuturesQryBankCashInBIDV.txt',
      'form_frmFuturesQryBankCashInAdd.txt',
      'query_FuturesQryBankCashIn.txt',
    ],
    notes: [
      'Vietinbank and MBbank use frmFuturesQryBankCashIn.',
      'BIDV uses frmFuturesQryBankCashInBIDV.',
      'Delete soft-deletes public_bank_cash by bank/date.',
    ],
  },
  'bank-statement': {
    title: 'Bank Statement',
    accessLabel: 'Bank Statement',
    target: 'frmFuturesMainSubFormBank -> frmFuturesQryBankStatementVTB/MBB/BIDV',
    description:
      'Bank statement list using the shared bank subform. Bank selection controls which statement list and add form are active.',
    evidence: [
      'form_frmFuturesMainSubFormBank.txt',
      'form_frmFuturesQryBankStatementVTB.txt',
      'form_frmFuturesQryBankStatementMBB.txt',
      'form_frmFuturesQryBankStatementBIDV.txt',
      'form_frmFuturesQryBankStatementVTBAdd.txt',
      'form_frmFuturesQryBankStatementMBBAdd.txt',
      'form_frmFuturesQryBankStatementBIDVAdd.txt',
      'query_FuturesQryBankStatement.txt',
    ],
    notes: [
      'Vietinbank, MBbank, and BIDV have separate list/add forms.',
      'Model selected bank explicitly in web instead of relying on FUTURES_SCREEN_NAME quirks.',
      'Delete soft-deletes public_bank_statement by bank/date.',
    ],
  },
} satisfies Record<BankMode, { title: string; accessLabel: string; target: string; description: string; evidence: string[]; notes: string[] }>

export default function FuturesBankView({ mode }: Props) {
  const [bank, setBank] = useState<BankName>('Vietinbank')
  const meta = modeMeta[mode]

  return (
    <FuturesEvidencePanel
      title={meta.title}
      accessLabel={meta.accessLabel}
      accessTarget={meta.target}
      description={meta.description}
      actions={['New', 'Edit', 'Delete']}
      evidence={meta.evidence}
      notes={meta.notes}
    >
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
        <Typography variant="body2" fontWeight={700}>
          Bank:
        </Typography>
        <TextField
          select
          size="small"
          value={bank}
          onChange={(event) => setBank(event.target.value as BankName)}
          sx={{ width: 160 }}
        >
          <MenuItem value="Vietinbank">Vietinbank</MenuItem>
          <MenuItem value="MBbank">MBbank</MenuItem>
          <MenuItem value="BIDV">BIDV</MenuItem>
        </TextField>
        <Typography variant="body2" color="text.secondary">
          Selected bank will drive the backend query/form mapping.
        </Typography>
      </Stack>
    </FuturesEvidencePanel>
  )
}
